// Get an image from the user, by whichever route they already have it.
//
// Two routes, because people arrive with the image in two different states. A
// file on disk wants the file dialog. An image already on the clipboard — which
// is how these actually arrive, copied straight out of PowerPoint — wants a
// paste, and making that person save a temporary PNG first is the whole reason
// icons used to be added by hand.
//
// WHY THE PASTE EVENT AND NOT navigator.clipboard.read().
// The async Clipboard API triggers a browser permission prompt, and a prompt in
// the middle of "add an icon" is a dead end for anyone who clicks Block once.
// The paste EVENT carries the image with no prompt at all, because the user
// pressing Ctrl+V IS the permission. The cost is that we only receive it while
// something is listening, which is why the zone is explicit: it opens, it says
// it is waiting, it listens at the document so focus does not matter, and it
// closes again.
//
// Extracted from IconPicker, which had all of this working already. It is here
// so the icons admin screen, the shapes admin screen and the object picker are
// one implementation rather than three that quietly diverge.
import { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Upload, ClipboardPaste, X, Loader2 } from 'lucide-react'

const DEFAULT_ALLOWED = ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp']

interface ImageIntakeProps {
    /** Called with a validated image file, from the file dialog, a paste, or a drop. */
    onFile: (file: File) => void | Promise<void>
    /** `accept` for the file input. Defaults to the allowed MIME list. */
    accept?: string
    /** MIME types to accept. A file outside this list is rejected inline, not silently. */
    allowedTypes?: string[]
    disabled?: boolean
    /** Show a spinner on the buttons while the caller processes the file. */
    busy?: boolean
    chooseLabel?: string
    /**
     * Hide the file button. For a screen that already has its own file input —
     * the shapes toolbar takes several files at once, which this deliberately
     * does not — so the only thing missing there is the paste.
     */
    showChooseButton?: boolean
    className?: string
}

export function ImageIntake({
    onFile,
    accept,
    allowedTypes = DEFAULT_ALLOWED,
    disabled = false,
    busy = false,
    chooseLabel,
    showChooseButton = true,
    className,
}: ImageIntakeProps) {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [pasteMode, setPasteMode] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const accepts = useCallback(
        (file: File) => {
            // An SVG dragged from some apps arrives with an empty type; fall back to the
            // extension rather than rejecting something the user can plainly see is an image.
            if (file.type) return allowedTypes.includes(file.type)
            return /\.(png|jpe?g|gif|svg|webp)$/i.test(file.name)
        },
        [allowedTypes]
    )

    const accept_ = useCallback(
        (file: File | null | undefined) => {
            if (!file) {
                setError(t('imageIntake.none', 'No image found — copy an image, then paste.'))
                return
            }
            if (!accepts(file)) {
                setError(t('imageIntake.wrongType', 'That wasn’t a supported image. Use PNG, JPG, GIF, SVG or WebP.'))
                return
            }
            setError(null)
            setPasteMode(false)
            void onFile(file)
        },
        [accepts, onFile, t]
    )

    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            accept_(e.target.files?.[0])
            // Reset so choosing the SAME file again still fires a change event.
            if (fileInputRef.current) fileInputRef.current.value = ''
        },
        [accept_]
    )

    // While the zone is open, capture Ctrl+V / Cmd+V at the document level so it works
    // regardless of where focus happens to be — and without a permission prompt.
    useEffect(() => {
        if (!pasteMode) return
        const onPaste = (e: ClipboardEvent) => {
            const item = Array.from(e.clipboardData?.items || []).find((it) => it.type.startsWith('image/'))
            if (item) {
                e.preventDefault()
                accept_(item.getAsFile())
            } else {
                setError(
                    t(
                        'imageIntake.clipboardEmpty',
                        'No image on your clipboard — copy an image (e.g. from PowerPoint) and paste again.'
                    )
                )
            }
        }
        document.addEventListener('paste', onPaste)
        return () => document.removeEventListener('paste', onPaste)
    }, [pasteMode, accept_, t])

    // Escape closes the zone. Without it the only way out is the small X, and a zone
    // that says "waiting for a paste" with no obvious exit reads as stuck.
    useEffect(() => {
        if (!pasteMode) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setPasteMode(false); setError(null) }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [pasteMode])

    return (
        <div className={cn('space-y-2', className)}>
            <div className="flex items-center gap-2">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept ?? allowedTypes.join(',')}
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={disabled || busy}
                />
                {showChooseButton && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={disabled || busy}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        {chooseLabel ?? t('imageIntake.choose', 'Choose file')}
                    </Button>
                )}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled || busy}
                    onClick={() => { setError(null); setPasteMode((v) => !v) }}
                >
                    <ClipboardPaste className="h-4 w-4 mr-2" />
                    {t('imageIntake.paste', 'Paste image')}
                </Button>
            </div>

            {pasteMode && (
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); accept_(e.dataTransfer?.files?.[0]) }}
                    className="relative rounded-md border-2 border-dashed border-muted-foreground/40 bg-muted/30 px-4 py-6 text-center"
                >
                    <button
                        type="button"
                        onClick={() => { setPasteMode(false); setError(null) }}
                        className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:bg-muted"
                        aria-label={t('common.close', 'Close')}
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                    <ClipboardPaste className="mx-auto h-5 w-5 text-muted-foreground" />
                    <p className="mt-2 text-sm font-medium">
                        {t('imageIntake.zoneTitle', 'Press Ctrl+V to paste your image')}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {t('imageIntake.zoneHint', 'or drop an image file here')}
                    </p>
                </div>
            )}

            {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
    )
}
